/* One day, and where in it you can listen.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS HAS A PLAYHEAD WHEN DayTimeline DELIBERATELY DOES NOT
 *
 * DayTimeline's header is explicit:
 *
 *   "There is deliberately no playhead. A scrubber says 'any moment can be
 *    played', and on this fleet that is false."
 *
 * That was correct when it was written. `audio` uploads only at `full` tier,
 * the fleet default is `events`, and so the only playable things on a normal
 * day were the clips somebody had chosen to save. A scrub bar over that would
 * have been an invitation to click on nothing.
 *
 * It stopped being the whole truth when a standing archive job began pulling
 * the node's own 64 kbit proxy. There are now thirty unbroken hours of it in
 * storage for one mission, and every second of that is playable.
 *
 * So the rule is obeyed rather than overruled: **the strip takes a click only
 * where `cloud_seconds > 0`.** Solid is reachable. Hatched is on the node's
 * card, which no request from a browser can reach, and is not a target. Empty
 * is a gap and is drawn rather than left blank, because rule 4 says a gap is a
 * fact. The promise a scrubber makes is now kept exactly where it is made.
 *
 * ---------------------------------------------------------------------------
 * THE DAY IS THE LISTENER'S DAY
 *
 * The endpoint buckets by UTC date. In summer Norway is two hours ahead, so a
 * local day straddles two UTC days and one request cannot answer for it. The
 * page fetches both and hands the merged hours here; this component works
 * entirely in epoch seconds and asks time.ts where the boundaries are, so it
 * does not need to know which clock is in force.
 */
import { useState } from 'react'
import { useT } from '../lang'
import { clock, dayBounds, hourFraction } from '../time'
import type { DayClip } from './DayTimeline'

/** One hour of the day, as `/day` reports it, with its instant resolved. */
export interface StripHour {
  /** Epoch seconds at the start of this hour. */
  t: number
  cloud_seconds: number
  node_seconds: number
}

const HOUR = 3600

const pct = (n: number) => `${Math.max(0, Math.min(100, n * 100))}%`

export function DayStrip({
  day, hours, clips, at, onSeek, onOpenClip,
}: {
  /** `YYYY-MM-DD` in the active zone. */
  day: string
  hours: StripHour[]
  clips: DayClip[]
  /** Playhead, epoch seconds, or null when nothing is loaded. */
  at: number | null
  /** Called with epoch seconds. Only ever fired from a reachable hour. */
  onSeek: (epoch: number) => void
  onOpenClip: (clip: DayClip) => void
}) {
  const t = useT()
  /* What is under the pointer, to the nearest five minutes.
   *
   * Rounded rather than exact because the value is only ever read, never used:
   * it answers "what time is here" while a finger is moving, and a label
   * changing sixty times a second is unreadable. Rounding also means the state
   * settles, so most pointer moves do not re-render at all. */
  const [hover, setHover] = useState<number | null>(null)
  const [t0, t1] = dayBounds(day)
  const span = t1 - t0
  const byHour = new Map(hours.map((h) => [Math.floor((h.t - t0) / HOUR), h]))
  // A local day is 23 hours in March and 25 in October. Drawing a fixed 24
  // would put every mark an hour out for half a year in one direction.
  const count = Math.round(span / HOUR)
  const reachable = hours.some((h) => h.cloud_seconds > 0)

  const seekTo = (fraction: number) => {
    const target = t0 + fraction * span
    const h = byHour.get(Math.floor((target - t0) / HOUR))
    // Refused rather than clamped to the nearest playable hour. A scrub that
    // silently lands somewhere else is worse than one that does nothing: the
    // second is a control the listener learns the shape of, the first is a
    // control that lies about where they are.
    if (!h || h.cloud_seconds <= 0) return
    onSeek(target)
  }

  return (
    <div className="daystrip">
      <div
        className="ds-bar"
        role={reachable ? 'slider' : 'img'}
        tabIndex={reachable ? 0 : -1}
        aria-label={t('Where in the day there is sound')}
        aria-valuemin={reachable ? 0 : undefined}
        aria-valuemax={reachable ? count : undefined}
        aria-valuenow={reachable && at != null
          ? Math.round((at - t0) / HOUR) : undefined}
        aria-valuetext={reachable && at != null ? clock(at) : undefined}
        onKeyDown={(e) => {
          if (!reachable || at == null) return
          // Ten minutes an arrow, an hour with a modifier. A sea noise worth
          // finding is rarely more precise than that, and a keystroke that
          // moves one second makes the whole day unreachable.
          const step = e.shiftKey ? HOUR : 600
          if (e.key === 'ArrowLeft') onSeek(Math.max(t0, at - step))
          else if (e.key === 'ArrowRight') onSeek(Math.min(t1, at + step))
          else return
          e.preventDefault()
        }}
        onClick={(e) => {
          const box = e.currentTarget.getBoundingClientRect()
          seekTo((e.clientX - box.left) / box.width)
        }}
        onPointerMove={(e) => {
          const box = e.currentTarget.getBoundingClientRect()
          const at = t0 + ((e.clientX - box.left) / box.width) * span
          const round = Math.round(at / 300) * 300
          setHover((h) => (h === round ? h : round))
        }}
        onPointerLeave={() => setHover(null)}
      >
        {Array.from({ length: count }, (_, i) => {
          const h = byHour.get(i)
          // Proportional, never squared off. The oldest hour on a card is
          // normally half-eaten by the janitor and the current one is still
          // filling; both are the steady state.
          const cloud = h ? Math.min(1, h.cloud_seconds / HOUR) : 0
          const node = h ? Math.min(1, h.node_seconds / HOUR) : 0
          return (
            <div
              key={i}
              className={`ds-hour${cloud > 0 ? ' has' : ''}`}
              title={hourTitle(t, h, t0 + i * HOUR)}
            >
              <span className="ds-gap" />
              {node > 0 && <span className="ds-node" style={{ height: pct(node) }} />}
              {cloud > 0 && <span className="ds-cloud" style={{ height: pct(cloud) }} />}
            </div>
          )
        })}

        {/* A clip is seconds long against a whole day, so it is a mark rather
            than a band -- drawn to scale it would be invisible. */}
        {clips.map((c) => (
          <button
            key={c.clip_id}
            type="button"
            className="ds-clip"
            style={{ left: pct(hourFraction(new Date(c.started_utc).getTime() / 1000)) }}
            onClick={(e) => { e.stopPropagation(); onOpenClip(c) }}
            aria-label={`${t('Recording')} ${clock(new Date(c.started_utc).getTime() / 1000)}`}
            title={`${clock(new Date(c.started_utc).getTime() / 1000)} · ${c.duration_s.toFixed(0)} s`}
          />
        ))}

        {at != null && at >= t0 && at <= t1 && (
          <i className="ds-head" style={{ left: pct((at - t0) / span) }} aria-hidden="true" />
        )}

        {/* Where the pointer is, said in clock time. A twenty-four-hour bar is
            three hundred pixels wide on a phone, so without this the only way
            to find out what a column is is to press it. */}
        {hover != null && hover >= t0 && hover <= t1 && (
          <>
            <i className="ds-hover" style={{ left: pct((hover - t0) / span) }}
              aria-hidden="true" />
            <span className="ds-hovertime num"
              style={{ left: pct((hover - t0) / span) }} aria-hidden="true">
              {clock(hover)}
            </span>
          </>
        )}
      </div>

      <div className="ds-axis" aria-hidden="true">
        {Array.from({ length: 5 }, (_, i) => (
          <span key={i} style={{ left: pct((i * 6) / count) }}>
            {String(Math.min(i * 6, count)).padStart(2, '0')}
          </span>
        ))}
      </div>

      {/* The key.
       *
       * Solid, hatched and a diamond were three states nothing on the page
       * named. On a fleet where most of a day is normally missing, an
       * unexplained hatch reads as damage rather than as "nothing was recorded
       * here" -- and the difference between those two is the whole point of
       * drawing gaps at all. Entries appear only for states actually on
       * screen: a legend explaining something that is not there is one more
       * thing to read for nothing. */}
      <ul className="ds-key">
        {reachable && <li><i className="k-cloud" />{t('You can listen')}</li>}
        {hours.some((h) => !h.cloud_seconds && h.node_seconds > 0) && (
          <li><i className="k-node" />{t('On the listening post')}</li>
        )}
        <li><i className="k-gap" />{t('Nothing recorded')}</li>
        {clips.length > 0 && <li><i className="k-clip" />{t('Recording')}</li>}
      </ul>
    </div>
  )
}

function hourTitle(
  t: (k: string) => string, h: StripHour | undefined, start: number,
): string {
  const when = clock(start)
  if (!h || (!h.cloud_seconds && !h.node_seconds)) return `${when} — ${t('nothing recorded')}`
  const bits: string[] = []
  if (h.cloud_seconds) bits.push(`${Math.round(h.cloud_seconds / 60)} min`)
  // Said plainly rather than as a state name. "On the node" is a sentence
  // about our architecture; "not in the cloud yet" is a sentence about whether
  // pressing here will produce sound.
  if (!h.cloud_seconds && h.node_seconds) bits.push(t('on the listening post, not here'))
  return `${when} — ${bits.join(', ')}`
}
